import {
	Component,
	ElementRef,
	EventEmitter,
	HostListener,
	Injector,
	Input,
	OnChanges,
	OnDestroy,
	OnInit, QueryList,
	SimpleChanges, ViewChild, ViewChildren,
} from '@angular/core';
import { ProjectGuardService } from '@app/components/cleanui/system/Guard/project.guard';
import { AbstractQuestionViewComponent } from '@app/pages/questions/abstract-question-view.component';
import { WizardPage } from './interfaces';
import { MessplatzService } from '@app/services/messplatz/messplatz.service';
import { INavTreeNode } from '@app/workflow/components/navigator/nav-tree/interfaces';
import { NavTreeNavigator } from '@app/workflow/components/navigator/nav-tree/nav-tree-navigator';
import { SessionItemData } from '@app/workflow/services/session-item-data';
import { Messgeraete } from '@interfaces/interfaces';
import { BlockQuestionItem, ChoiceQuestion } from '@interfaces/questions';
import { combineLatest, Subscription } from 'rxjs';
import { tap } from 'rxjs/operators';
import { DeveloperService } from '@app/services/developer/developer.service';
import { SpecialQuestionService } from '@app/workflow/components/questionnaire/questionnaire-item/special-question/special-question.service';
import { first, last, isNil } from 'lodash';
import { GroupStatus } from '@app/pages/questions/question-status.service';
import { InputService, TouchSwipe } from '@app/services/input/input.service';
import { NzButtonComponent } from 'ng-zorro-antd/button';
import { TWorkflowViewMode } from '@workflow/components/navigator/interfaces';
import { WorkflowItemQueriesService } from '@workflow/queries/services/workflow-item-queries.service';

@Component({
	selector: 'workflow-wizard-questionpage',
	templateUrl: './wizard-view.component.html',
	styleUrls: ['./wizard-view.component.scss'],
	providers: [
		ProjectGuardService,
		WorkflowItemQueriesService,
	],
})
export class WizardViewComponent extends AbstractQuestionViewComponent implements OnChanges, OnInit, OnDestroy {

	@Input()
	currentNode: INavTreeNode;

	@Input()
	page: WizardPage;

	hasNextPage: boolean;
	hasPreviousPage: boolean;
	isQuestionGroupSkippable: boolean;
	nextQuestionGroupUrl: string;
	showSkipButton: boolean;
	nextText = 'actions.next';
	hasFullHeight: boolean;
	isVisibleMissingDevicesModal: boolean;
	hasOnlyChoiceQuestion: boolean;

	suppressedByFehlwert = false;

	protected viewMode: TWorkflowViewMode = 'wizard';

	private page$ = new EventEmitter<WizardPage>();

	private subscription = new Subscription();
	nextNode: INavTreeNode;
	prevNode: INavTreeNode;
	nextPageUrl: string;
	prevPageUrl: string;
	restartPage: INavTreeNode;
	restartUrl: string;
	restartText: string;
	hasRestartPage: boolean;
	showAsterisk: boolean;
	description: string;
	missingDevices: Messgeraete[];
	hasItemTexts: boolean;
	epilogUrlSlug: string;
	prologUrlSlug: string;
	currentUrlSlug: string;
	nextUrlSlug: string;
	disableNextButton: boolean;

	allNodesOfItemDisabled: boolean;

	private isDemoMode: boolean;

	pageTitle: string;
	questionGroupStatus: GroupStatus;
	public overwriteDescriptionText: string;
	private swipePreviewNext: NzButtonComponent;
	private swipePreviewNextButtons: QueryList<NzButtonComponent>;
	@ViewChild('swipePreviewPrevious') swipePreviewPrevious: NzButtonComponent;
	@ViewChild('nextHover') nextHover: ElementRef;
	@ViewChild('previousHover') previousHover: ElementRef;

	hasAnyMissingValueKey = false;

	@ViewChildren('swipePreviewNext')
	set swipePreviewNext_(buttons: QueryList<NzButtonComponent>) {
		this.swipePreviewNextButtons = buttons;
		this.updateSwipePreviewNext();
	}

	constructor(
		injector: Injector,
		protected projectGuardService: ProjectGuardService,
		private messplatzService: MessplatzService,
		private navigator: NavTreeNavigator,
		private specialQuestionService: SpecialQuestionService,
		private inputService: InputService,
		developerService: DeveloperService,
	) {
		super(injector);
		this.subscription.add(developerService.isDemoMode$.subscribe(isDemoMode => this.isDemoMode = isDemoMode));
		this.subscription.add(this.inputService.vitaReactPush$.subscribe(timestamp => {
			if (timestamp) {
				this.nextPage();
			}
		}));
		this.subscription.add(this.inputService.swipeDetected$.subscribe(touchSwipe => {
			if (touchSwipe) {
				if (touchSwipe.moveVector.x < 0) {
					this.nextPage();
				} else if (this.hasPreviousPage) {
					this.router.navigate([this.prevPageUrl]).catch(console.error);
				}
			}
		}));
		this.subscription.add(this.inputService.currentTouch$.subscribe(currentTouch => {
			this.updateSwipePreview(currentTouch);
		}));
	}

	/**
	 * Prolog Seite
	 */
	get isProlog(): boolean {
		return this.page?.question?.type === 'prolog' || this.page?.question?.type === 'preface';
	}

	/**
	 * Epilog Seite
	 */
	get isEpilog(): boolean {
		return this.page?.question?.type === 'epilog';
	}

	/**
	 * Inhaltsseite (kein Prolog, kein Epilog)
	 */
	get isContentPage(): boolean {
		return !this.isProlog && !this.isEpilog;
	}

	/**
	 * Frage ist eine SpecialQuestion
	 */
	get isSpecialQuestion(): boolean {
		return this.individuelleFragetypen.includes(this.page?.question?.type);
	}

	/**
	 * Frage ist ein bekannter Frage-Typ (BlockQuestion oder SpecialQuestion)
	 */
	get isQuestionType(): boolean {
		return this.alleFrageTypen.includes(this.page?.question?.type);
	}

	get isAlternativeInputPage(): boolean {
		return this.currentUrlSlug.includes('/a/');
	}

	ngOnInit(): void {
		super.ngOnInit();

		const pipe = combineLatest([
			this.page$,
			this.workflowItemDirective.current$,
			this.hasWorkflowWritePermission$
		]).pipe(
			tap(([, item,]) => this.finalPipeAction(item)),
		);
		this.subscription.add(pipe.subscribe());

		this.subscription.add(this.page$.subscribe(page => {
			this.pageTitle = !isNil(this.page.question?.title) ? this.page.question.title : this.page.title;
		}));

		this.page$.emit(this.page);
	}

	ngOnDestroy(): void {
		this.subscription.unsubscribe();

		super.ngOnDestroy();
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes.page) {
			this.page$.emit(this.page);
		}
	}

	@HostListener('window:keydown', ['$event'])
	keyPress($event: KeyboardEvent): void {
		if (this.isDemoMode && $event.code === 'IntlBackslash') {
			window.dispatchEvent(new Event('vitareact-press'));
			window.dispatchEvent(new Event('vitareact-release'));
			$event.preventDefault();
		}
	}

	@HostListener('window:vitareact-press', [])
	vitaReactPress(): void {
		this.inputService.newVitaReactPush();
	}

	@HostListener('window:touchstart', ['$event'])
	touchStart($event: TouchEvent): void {
		this.inputService.touchStart($event);
	}

	@HostListener('window:touchmove', ['$event'])
	touchMove($event: TouchEvent): void {
		this.inputService.touchMove($event);
	}

	@HostListener('window:touchend')
	touchEnd(): void {
		this.inputService.touchEnd();
	}

	private getQuestionGroup(current: INavTreeNode): INavTreeNode {
		while (current) {
			if (current.isQuestionGroup)
				return current;
			current = current.parent;
		}
		return null;
	}

	protected finalPipeAction(data: SessionItemData): void {
		this.disableNextButton = false;
		if (this.page && this.currentNode && this.workflowItemDirective) {
			this.hasAnyMissingValueKey = !!(this.page.missingValueKey || this.page.missingValueKeyGroup || this.page.missingValueKeys);
			if (!this.hasAnyMissingValueKey) {
				this.suppressedByFehlwert = false;
			}
			this.nextText = this.page.nextButtonOverwrite || 'actions.next';

			this.isQuestionGroupSkippable = this.page.isWizardSkipAllowed;
			this.hasFullHeight = !!this.page.fullHeight;
			if (this.page.question?.type === 'BlockQuestion') {
				const bq = this.page.question as BlockQuestionItem;
				this.checkHasOnlyChoiceQuestion(bq);
				this.hasFullHeight = bq.items.some(item => item.question?.type === 'range') || bq.default?.type === 'range';
			}

			this.nextNode = this.navigator.nextPage(this.currentNode, data);
			this.nextPageUrl = this.navigator.getUrl(this.nextNode, this.workflowItem.id);

			if (this.currentNode.requiredDevices?.length) {
				this.missingDevices = this.messplatzService.getMissingDevices(this.currentNode.requiredDevices as Messgeraete[]);
			} else {
				this.missingDevices = undefined;
			}

			const currentProcedure = this.currentNode.isProcedure && this.navigator.getProcedureRoot(this.currentNode);
			const completedProcedure = this.currentNode.isProcedure && currentProcedure?.isCompleted(data);
			if (this.currentNode.isProlog && this.currentNode.isProcedure && !this.finishedAndLocked) {
				if (completedProcedure) {
					this.restartPage = this.navigator.nextPage(this.currentNode, data, { restart: true });
					this.restartUrl = this.navigator.getUrl(this.restartPage, this.workflowItem.id);
					this.restartText = this.page.restartButtonOverwrite || 'actions.restart';
					if (this.missingDevices?.length) {
						this.restartText = 'workflow.newOpenManualInput';
					} else {
						this.restartText = 'actions.restartTest';
					}
				} else {
					this.disableNextButton = !this.hasWorkflowWritePermission;
					this.restartPage = null;
					if (this.missingDevices?.length) {
						this.nextText = 'workflow.openManualInput';
					} else {
						this.nextText = !isNil(this.page.startText) ? this.page.startText : 'actions.starttest';
					}
				}
			} else {
				this.restartPage = null;
			}

			if (completedProcedure && !this.currentNode.isProlog) {
				this.showAsterisk = true;
			} else {
				this.showAsterisk = false;
			}

			this.prevNode = this.navigator.prevPage(this.currentNode, data);
			this.prevPageUrl = this.navigator.getUrl(this.prevNode, this.workflowItem.id);

			this.hasNextPage = !!this.nextNode;
			this.hasPreviousPage = !!this.prevNode;
			this.hasRestartPage = !!this.restartPage;
			this.isLastPageOfQuestionGroup = !this.hasNextPage || this.getQuestionGroup(this.currentNode) !== this.getQuestionGroup(this.nextNode);
			if (this.isLastPageOfQuestionGroup) {
				this.nextText = 'actions.next_question_group';
			}

			if (this.isQuestionGroupSkippable && !this.currentNode.isCompleted(data)) {
				const nextQuestionGroupNode = this.navigator.nextGroup(this.currentNode, data);
				this.nextQuestionGroupUrl = this.navigator.getUrl(nextQuestionGroupNode, this.workflowItem.id);
				this.showSkipButton = !!this.nextQuestionGroupUrl;
			} else {
				this.nextQuestionGroupUrl = null;
				this.showSkipButton = false;
			}

			this.currentUrlSlug = this.currentNode?.urlPrefixes?.[0];
			this.nextUrlSlug = this.nextNode?.urlPrefixes?.[0];
			this.epilogUrlSlug = this.navigator.findNextEpilog(this.currentNode, data)?.urlPrefixes?.[0];
			this.prologUrlSlug = this.navigator.findPrevProlog(this.currentNode, data)?.urlPrefixes?.[0];

			const itemTexts = this.getMergedItemTexts();
			this.hasItemTexts = !!itemTexts;
			if (this.page.question.description?.length && this.hasItemTexts) {
				this.description = this.page.question.description + ' - ' + itemTexts;
			} else if (this.hasItemTexts) {
				this.description = itemTexts;
			} else {
				this.description = this.page.question.description || '';
			}

			this.allNodesOfItemDisabled = this.navigator.areChildrenDisabled(this.currentNode.parent, data);
			this.isAllValid = this.isAllValid || this.allNodesOfItemDisabled;
			this.questionGroupStatus = this.questionStatusService.checkQuestionGroupStatus(this.page, data, [this.page.question]);
			this.isPageSufficientFilled = this.finishedAndLocked || this.questionGroupStatus.isSufficient || this.questionGroupStatus.isSkippable;
			this.updateSwipePreviewNext();
		}
	}

	private getMergedItemTexts(): string {
		const blockQuestion = this.page.question as BlockQuestionItem;
		if (this.page.question.type === 'BlockQuestion' && blockQuestion.options?.wizard?.splitBlock) {
			return blockQuestion.items.filter(i => i.text).map(item => item.text).join(' ');
		}
		return undefined;
	}

	showMissingDevicesModal(): void {
		this.isVisibleMissingDevicesModal = true;
	}
	closeMissingDevicesModal(): void {
		this.isVisibleMissingDevicesModal = false;
	}

	checkHasOnlyChoiceQuestion(questions: BlockQuestionItem): void {
		let hasChoiceQuestionLabels = false;
		for (const item of questions.items) {
			const question = item.question || questions.default;
			if (question.type === 'choice') {
				const choiceQuestion = question as ChoiceQuestion;
				hasChoiceQuestionLabels ||= (!!first(choiceQuestion.choices)?.label || !!last(choiceQuestion.choices)?.label);
			}
		}

		const hasOnlyChoiceQuestions = questions.items.every(item => (item.question || questions.default).type === 'choice');

		this.hasOnlyChoiceQuestion = hasOnlyChoiceQuestions && !hasChoiceQuestionLabels;
	}

	nextPage(): void {
		if (!this.specialQuestionService.isSpecialQuestion(this.page?.question?.type) || this.page?.allowNextPage) {
			if (this.showSkipButton) {
				this.router.navigate([this.nextQuestionGroupUrl]).catch(console.error);
				return;
			}

			const pageComplete = this.isProlog || (this.isPageSufficientFilled && this.isAllValid);
			if (this.hasNextPage && !this.page?.disableNext && pageComplete) {
				this.router.navigate([this.nextPageUrl]).catch(console.error);
				return;
			}

			if (!this.finishWorkflowSplitbutton.disabled) {
				this.doCompletion();
				return;
			}
		}
	}

	updateOverwriteDescriptionText(value: string): void {
		this.overwriteDescriptionText = value;
	}

	updateSwipePreview(currentTouch: TouchSwipe): void {
		const distance = currentTouch ? currentTouch.moveVector.x : 0;
		const nextButton = this.getButtonComponentAsElementRef(this.swipePreviewNext);
		const prevButton = this.getButtonComponentAsElementRef(this.swipePreviewPrevious);
		if (prevButton && distance > this.inputService.swipeDistance) {
			prevButton.nativeElement.style.cssText = 'background: #42baf9 !important; border-color: #42baf9 !important;';
			if (nextButton?.nativeElement) {
				nextButton.nativeElement.style.cssText = null;
			}
			if (this.previousHover?.nativeElement) {
				this.previousHover.nativeElement.classList.add('active');
			}
			if (this.nextHover?.nativeElement) {
				this.nextHover.nativeElement.classList.remove('active');
			}
		} else if (nextButton && distance < 0 && Math.abs(distance) > this.inputService.swipeDistance) {
			nextButton.nativeElement.style.cssText = 'background: #42baf9 !important; border-color: #42baf9 !important;';
			if (prevButton?.nativeElement) {
				prevButton.nativeElement.style.cssText = null;
			}
			if (this.previousHover?.nativeElement) {
				this.previousHover.nativeElement.classList.remove('active');
			}
			if (this.nextHover?.nativeElement) {
				this.nextHover.nativeElement.classList.add('active');
			}
		} else {
			if (prevButton?.nativeElement) {
				prevButton.nativeElement.style.cssText = null;
			}
			if (nextButton?.nativeElement) {
				nextButton.nativeElement.style.cssText = null;
			}
			if (this.previousHover?.nativeElement) {
				this.previousHover.nativeElement.classList.remove('active');
			}
			if (this.nextHover?.nativeElement) {
				this.nextHover.nativeElement.classList.remove('active');
			}
		}
	}

	/**
	 * Der gültige button der zur nächsten Seite führt
	 */
	updateSwipePreviewNext(): void {
		if (this.swipePreviewNextButtons && this.swipePreviewNextButtons.length) {
			const pageComplete = this.isProlog || (this.isPageSufficientFilled && this.isAllValid);
			if (this.showSkipButton) {
				this.swipePreviewNext = this.swipePreviewNextButtons.find(button => this.getButtonComponentAsElementRef(button).nativeElement.id === 'skipButton');
			} else if (this.hasNextPage && !this.page?.disableNext && pageComplete) {
				this.swipePreviewNext = this.swipePreviewNextButtons.find(button => this.getButtonComponentAsElementRef(button).nativeElement.id === 'nextButton');
			} else if (!this.finishWorkflowSplitbutton.disabled) {
				this.swipePreviewNext = this.swipePreviewNextButtons.find(button => this.getButtonComponentAsElementRef(button).nativeElement.id === 'finishButton');
			} else {
				this.swipePreviewNext = null;
			}
		}
	}

	private getButtonComponentAsElementRef(component: NzButtonComponent): ElementRef {
		if (!component) {
			return null;
		}
		return component['elementRef'] as ElementRef;
	}

	public updateInputValidity(valid: boolean, index: number, group: number): void {
		super.updateInputValidity(valid, index, group);
		this.updateSwipePreviewNext();
	}

	public fehlwertAdded(): void {
		if ((this.isSpecialQuestion && this.isContentPage) || this.isAlternativeInputPage) {
			this.router.navigate([this.nextPageUrl]).catch(console.error);
		}
	}

	statusFehlwert($event: boolean): void {
		this.suppressedByFehlwert = $event;
	}
}
